import type { CourseDocument, CourseDocumentState } from '@goschool/model'

export interface StateProps {
  transition:
    | 'ingestion'
    | 'pending'
    | 'quick_processing'
    | 'visual_processing'
    | 'finalizing'
    | 'visual_refinement'
    | 'preprocess_upload'
    | 'extract_content'
    | 'transcribe_audio'
    | 'content_analysis'
    | 'index_content'
  status: 'processing' | 'succeeded' | 'failed'
  progress: number | undefined
  total: number | undefined
  step: number
  label?: string
}

function toSafeNumber(value: unknown): number {
  const numberValue = Number(value ?? 0)
  return Number.isFinite(numberValue) ? numberValue : 0
}

function normalizePhase(phase: unknown): StateProps['transition'] {
  if (phase === 'visual_refinement') {
    return 'visual_processing'
  }
  if (
    phase === 'quick_processing' ||
    phase === 'visual_processing' ||
    phase === 'finalizing'
  ) {
    return phase
  }
  return 'quick_processing'
}

export function getDocumentState(document: CourseDocument): StateProps {
  const state: CourseDocumentState = document.state ?? {}
  const ingestionState = state.ingestion_state
  if (ingestionState != null) {
    const linearTotal = toSafeNumber(ingestionState.linear_progress_total_units)
    if (linearTotal > 0) {
      const linearCompleted = toSafeNumber(ingestionState.linear_progress_completed_units)
      const status: StateProps['status'] =
        linearCompleted >= linearTotal
          ? (toSafeNumber(ingestionState.failed_units) > 0 ? 'failed' : 'succeeded')
          : 'processing'
      const transition = normalizePhase(ingestionState.phase)
      return {
        transition,
        label: `document:status.${transition}`,
        status,
        progress: linearCompleted,
        total: linearTotal,
        step: 0,
      }
    }

    const quick = ingestionState.phases?.quick_processing
    const visual =
      ingestionState.phases?.visual_processing ?? ingestionState.phases?.visual_refinement
    const finalizing = ingestionState.phases?.finalizing
    if (quick != null || visual != null || finalizing != null) {
      const quickTotal = toSafeNumber(quick?.total_units ?? ingestionState.total_pages)
      const quickDone =
        toSafeNumber(quick?.completed_units) + toSafeNumber(quick?.failed_units)
      const visualTotal = toSafeNumber(visual?.total_units)
      const visualDone =
        toSafeNumber(visual?.completed_units) + toSafeNumber(visual?.failed_units)
      const finalizingTotal = toSafeNumber(finalizing?.total_units)
      const finalizingDone =
        toSafeNumber(finalizing?.completed_units) + toSafeNumber(finalizing?.failed_units)
      const done = quickDone + visualDone + finalizingDone
      const total = quickTotal + visualTotal + finalizingTotal

      const status: StateProps['status'] =
        total > 0 && done >= total
          ? (
            toSafeNumber(quick?.failed_units) +
              toSafeNumber(visual?.failed_units) +
              toSafeNumber(finalizing?.failed_units) >
            0
            ? 'failed'
            : 'succeeded'
          )
          : 'processing'

      const transition = normalizePhase(ingestionState.phase)

      return {
        transition,
        label: `document:status.${transition}`,
        status,
        progress: done,
        total,
        step: 0,
      }
    }

    const total = Number(ingestionState.total_units ?? ingestionState.total_pages ?? 0)
    const completed = Number(
      ingestionState.completed_units ?? ingestionState.completed_pages ?? 0
    )
    const failed = Number(ingestionState.failed_units ?? ingestionState.failed_pages ?? 0)
    const done = completed + failed
    const status: StateProps['status'] =
      total > 0 && done >= total
        ? (failed > 0 ? 'failed' : 'succeeded')
        : 'processing'
    return {
      transition: 'ingestion',
      label: 'document:status.ingestion',
      status,
      progress: done,
      total,
      step: 0,
    }
  }

  type LegacyTransition = Exclude<keyof CourseDocumentState, 'ingestion_state'>
  const transitions: LegacyTransition[] = [
    'preprocess_upload',
    'extract_content',
    'transcribe_audio',
    'content_analysis',
    'index_content',
  ]

  let step = 0
  for (const key of transitions) {
    const keyState = state[key]
    if (keyState?.status === 'succeeded') {
      step++
    }
    if (
      keyState != null &&
      (keyState.status !== 'succeeded' || key === 'index_content')
    ) {
      const { progress, status, total } = keyState

      return {
        transition: key,
        label: `document:status.${key}`,
        status,
        progress,
        total,
        step,
      }
    }
  }

  return {
    transition: 'pending',
    label: 'document:status.pending',
    status: 'processing',
    step,
    progress: undefined,
    total: undefined,
  }
}
